import { memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router'

// Actions
import { actions as audioActions } from 'modules/audio/actions'

// Components
import { ContentHeader } from 'kit'
import { Error, Layout } from '@tracklib/kit'
import InfoItem2 from 'components/base/infoitem2'
import SEO from 'components/base/seo'
import TrackList from 'components/browse/tracklist'
import Filter from 'components/filter'

// Hooks
import { useSearchQuery } from 'modules/browse/hooks'
import { useRequest } from 'modules/requests/hooks'

// Types
import { Genre, EntityState, State } from 'types'

const GenrePage = () => {
  const { slug } = useParams() as { slug: string }
  const playAllRequest = useRequest('PREPARE_PLAY_SEARCH_QUERY')
  const dispatch = useDispatch()
  const genres = useSelector<State, EntityState<Genre>>(state => state.entities.genres)
  const genreList = Object.keys(genres).map(id => genres[id])
  const genre = genreList.filter(g => g.slug === slug)[0]

  const children = genre ? genreList.filter(g => g.parent === genre.id) : []
  const parent = genre && genre.parent && genreList.filter(g => g.id === genre.parent)
  const related = genre?.parent ? genreList.filter(g => g.id !== genre.id && g.parent && g.parent === genre.parent) : []

  const queryObj = { genres: [slug] }
  const songListProps = useSearchQuery(queryObj)

  if (!genre) {
    return <Error message="No such genre" />
  }
  const breadcrumbs = [
    {
      text: 'Genres',
      link: '/genres/',
    },
    {
      text: genre.name,
      link: `/genres/${genre.slug}`,
    },
  ]

  if (parent) {
    breadcrumbs.splice(1, 0, {
      text: parent[0]?.name,
      link: `/genre/${parent[0]?.slug}`,
    })
  }

  return (
    <>
      <SEO
        title={`${genre.name} samples on Tracklib`}
        description={genre.description || `${genre.name}, a genre on Tracklib`}
        image={`https:${genre.image}`}
        imageWidth={550}
        imageHeight={550}
        type="music.genre"
      />
      <Layout.FullWidth>
        <ContentHeader
          playAll={() => dispatch(audioActions.playSearchQuery({ genres: [genre.id] }))}
          playAllRequest={playAllRequest}
          image={genre.image}
          type="Genre"
          title={genre.name}
          description={genre.description}
          infoItems={[
            ...(children && children.length > 0 ? [<InfoItem2 title="Sub-genres" genres={children} showAll displayAsRow />] : []),
            ...(related && related.length > 0 ? [<InfoItem2 title="Related genres" genres={related} />] : []),
          ]}
          sharePath={`/genre/${genre.slug}`}
          adminEditPath={`/tracks/genre/${genre.id}/change`}
          breadcrumbs={breadcrumbs}
        />
        <Filter queryObj={queryObj} />
        <TrackList {...songListProps} />
      </Layout.FullWidth>
    </>
  )
}

export default memo(GenrePage)
